import React, { useEffect, useRef, useState } from 'react'
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material'
import {
    findUniqueEvidenceMatch,
    resolvePdfPageNumber
} from './qualityReviewSource'
import {
    buildBoundedPdfViewport,
    buildSecurePdfDocumentOptions,
    createPdfPreviewError,
    hasPdfHeader,
    PDF_SOURCE_MAX_FILE_BYTES,
    readBoundedPdfTextContent,
    safePdfPreviewMessage,
    validatePdfPageReference,
    withPdfOperationTimeout
} from './qualityReviewPdfSecurity'

export default function PdfSourcePage({ source, onStatusChange }) {
    const canvasRef = useRef(null)
    const [renderState, setRenderState] = useState({
        status: 'loading',
        message: '',
        highlights: [],
        width: 0,
        height: 0,
        pageNumber: resolvePdfPageNumber(source),
        totalPages: 0
    })

    useEffect(() => {
        let cancelled = false
        let loadingTask = null
        let renderTask = null
        let pdfjs = null
        let pdfWorker = null
        let destroyPdfRuntime = null

        function stopPdfWork() {
            try {
                renderTask?.cancel()
            } catch (error) {
                // Rendering was already complete.
            }
            if (loadingTask) {
                const taskToDestroy = loadingTask
                loadingTask = null
                try {
                    Promise.resolve(taskToDestroy.destroy()).catch(() => {
                        // The worker may already have been terminated.
                    })
                } catch (error) {
                    // The loading task may already be closed.
                }
            }
            if (pdfWorker) {
                const workerToDestroy = pdfWorker
                pdfWorker = null
                destroyPdfRuntime?.(pdfjs, workerToDestroy)
            }
        }

        async function renderPdfPage() {
            if (!source?.file || !canvasRef.current) return
            setRenderState(prev => ({ ...prev, status: 'loading', message: '', highlights: [] }))

            try {
                const runtime = await import('./qualityReviewPdfRuntime')
                if (cancelled) return
                destroyPdfRuntime = runtime.destroyIsolatedPdfRuntime
                const isolatedRuntime = runtime.createIsolatedPdfRuntime()
                pdfjs = isolatedRuntime.pdfjs
                pdfWorker = isolatedRuntime.worker
                if (!pdfWorker || pdfjs.GlobalWorkerOptions.workerPort !== pdfWorker) {
                    throw createPdfPreviewError('PDF_WORKER_UNAVAILABLE')
                }
                if (Number(source.file.size || 0) > PDF_SOURCE_MAX_FILE_BYTES) {
                    throw createPdfPreviewError('PDF_FILE_TOO_LARGE')
                }
                const data = await withPdfOperationTimeout(source.file.arrayBuffer(), stopPdfWork)
                if (cancelled) {
                    stopPdfWork()
                    return
                }
                if (data.byteLength > PDF_SOURCE_MAX_FILE_BYTES) {
                    throw createPdfPreviewError('PDF_FILE_TOO_LARGE')
                }
                if (!hasPdfHeader(data)) {
                    throw createPdfPreviewError('PDF_INVALID_FILE')
                }
                loadingTask = pdfjs.getDocument(buildSecurePdfDocumentOptions(data, pdfjs))
                const document = await withPdfOperationTimeout(
                    loadingTask.promise,
                    stopPdfWork
                )
                if (cancelled) {
                    stopPdfWork()
                    return
                }

                const pageReference = validatePdfPageReference(source, document.numPages)
                if (!pageReference.valid) {
                    const nextState = {
                        status: 'error',
                        message: pageReference.message,
                        matchStatus: 'error',
                        highlights: [],
                        width: 0,
                        height: 0,
                        pageNumber: pageReference.requestedPage,
                        totalPages: document.numPages
                    }
                    setRenderState(nextState)
                    onStatusChange?.(nextState)
                    stopPdfWork()
                    return
                }
                const requestedPage = pageReference.requestedPage
                const pageNumber = requestedPage
                const page = await withPdfOperationTimeout(
                    document.getPage(pageNumber),
                    stopPdfWork
                )
                if (cancelled) {
                    stopPdfWork()
                    return
                }

                const canvas = canvasRef.current
                const context = canvas.getContext('2d', { alpha: false })
                if (!context) throw createPdfPreviewError('PDF_CANVAS_UNAVAILABLE')
                const deviceScale = Math.min(window.devicePixelRatio || 1, 2)
                const viewport = buildBoundedPdfViewport(page, { deviceScale })
                canvas.width = Math.floor(viewport.width * deviceScale)
                canvas.height = Math.floor(viewport.height * deviceScale)
                canvas.style.width = `${viewport.width}px`
                canvas.style.height = `${viewport.height}px`

                renderTask = page.render({
                    canvasContext: context,
                    viewport,
                    annotationMode: pdfjs.AnnotationMode.DISABLE,
                    transform: deviceScale === 1 ? null : [deviceScale, 0, 0, deviceScale, 0, 0]
                })
                await withPdfOperationTimeout(
                    renderTask.promise,
                    stopPdfWork
                )
                if (cancelled) {
                    stopPdfWork()
                    return
                }

                const boundedText = await withPdfOperationTimeout(
                    readBoundedPdfTextContent(page),
                    stopPdfWork
                )
                if (cancelled) {
                    stopPdfWork()
                    return
                }
                const match = boundedText.tooMany
                    ? { status: 'too_complex', indexes: [], target: '' }
                    : findUniqueEvidenceMatch(boundedText.items, source.highlightTexts || [])
                const documentPageLabel = boundedText.tooMany ? '' : detectDocumentPageLabel(boundedText.items, viewport)
                const highlights = ['exact', 'normalized'].includes(match.status)
                    ? buildHighlightRects(boundedText.items, match.indexes, viewport)
                    : []
                const nextState = {
                    status: 'ready',
                    message: '',
                    matchStatus: match.status,
                    highlights,
                    width: viewport.width,
                    height: viewport.height,
                    pageNumber,
                    requestedPage,
                    totalPages: document.numPages,
                    documentPageLabel
                }
                setRenderState(nextState)
                onStatusChange?.(nextState)
                await loadingTask.destroy()
                loadingTask = null
                destroyPdfRuntime(pdfjs, pdfWorker)
                pdfWorker = null
            } catch (error) {
                if (cancelled || error?.name === 'RenderingCancelledException') {
                    stopPdfWork()
                    return
                }
                const nextState = {
                    status: 'error',
                    message: safePdfPreviewMessage(error),
                    matchStatus: 'error',
                    highlights: [],
                    width: 0,
                    height: 0,
                    pageNumber: resolvePdfPageNumber(source),
                    totalPages: 0
                }
                setRenderState(nextState)
                onStatusChange?.(nextState)
                stopPdfWork()
            }
        }

        renderPdfPage()
        return () => {
            cancelled = true
            stopPdfWork()
        }
    }, [source, onStatusChange])

    return (
        <Box sx={{ height: '100%', overflow: 'auto', p: { xs: 1, md: 1.5 } }}>
            {renderState.status === 'loading' && (
                <Stack alignItems="center" justifyContent="center" spacing={1.25} sx={{ minHeight: 260 }}>
                    <CircularProgress size={30} />
                    <Typography variant="body2" color="text.secondary">Opening the referenced PDF page…</Typography>
                </Stack>
            )}
            {renderState.status === 'error' && (
                <Alert severity="warning" sx={{ maxWidth: 720, mx: 'auto', mt: 3 }}>
                    {renderState.message}
                </Alert>
            )}
            <Box
                sx={{
                    position: 'relative',
                    width: renderState.width || 0,
                    maxWidth: '100%',
                    mx: 'auto',
                    display: renderState.status === 'ready' ? 'block' : 'none',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 12px 30px rgba(21, 45, 58, 0.16)'
                }}
            >
                <Box
                    component="canvas"
                    ref={canvasRef}
                    sx={{ display: 'block', width: '100% !important', height: 'auto !important' }}
                />
                {renderState.highlights.map((rect, index) => (
                    <Box
                        key={`${rect.left}:${rect.top}:${index}`}
                        aria-label={renderState.matchStatus === 'exact' ? 'Exact evidence highlight' : 'Unique wording highlight'}
                        sx={{
                            position: 'absolute',
                            left: `${rect.left}%`,
                            top: `${rect.top}%`,
                            width: `${rect.width}%`,
                            height: `${rect.height}%`,
                            borderRadius: 0.75,
                            backgroundColor: 'rgba(255, 153, 67, 0.48)',
                            border: '2px solid rgba(216, 103, 13, 0.72)',
                            pointerEvents: 'none'
                        }}
                    />
                ))}
            </Box>
        </Box>
    )
}

function buildHighlightRects(items, indexes, viewport) {
    const indexSet = new Set(indexes)
    const rects = (items || [])
        .map((item, index) => ({ item, index }))
        .filter(entry => indexSet.has(entry.index))
        .map(({ item }) => textItemRect(item, viewport))
        .filter(Boolean)

    if (!rects.length) return []
    const left = Math.min(...rects.map(rect => rect.left))
    const top = Math.min(...rects.map(rect => rect.top))
    const right = Math.max(...rects.map(rect => rect.left + rect.width))
    const bottom = Math.max(...rects.map(rect => rect.top + rect.height))
    const padding = 2

    return [{
        left: percentage(Math.max(0, left - padding), viewport.width),
        top: percentage(Math.max(0, top - padding), viewport.height),
        width: percentage(Math.min(viewport.width, right + padding) - Math.max(0, left - padding), viewport.width),
        height: percentage(Math.min(viewport.height, bottom + padding) - Math.max(0, top - padding), viewport.height)
    }]
}

function textItemRect(item, viewport) {
    if (!item?.transform || !item?.str) return null
    const transform = multiplyTransforms(viewport.transform, item.transform)
    const height = Math.max(Math.hypot(transform[2], transform[3]), Number(item.height || 0) * viewport.scale, 8)
    const width = Math.max(Number(item.width || 0) * viewport.scale, 4)
    return {
        left: transform[4],
        top: transform[5] - height,
        width,
        height
    }
}

function multiplyTransforms(first, second) {
    return [
        first[0] * second[0] + first[2] * second[1],
        first[1] * second[0] + first[3] * second[1],
        first[0] * second[2] + first[2] * second[3],
        first[1] * second[2] + first[3] * second[3],
        first[0] * second[4] + first[2] * second[5] + first[4],
        first[1] * second[4] + first[3] * second[5] + first[5]
    ]
}

function percentage(value, total) {
    if (!total) return 0
    return Math.max(0, Math.min(100, (value / total) * 100))
}

function detectDocumentPageLabel(items, viewport) {
    const candidates = (items || [])
        .filter(item => /^\d{1,3}$/.test(String(item?.str || '').trim()))
        .map(item => ({ text: String(item.str).trim(), rect: textItemRect(item, viewport) }))
        .filter(candidate => {
            if (!candidate.rect) return false
            const centreX = candidate.rect.left + (candidate.rect.width / 2)
            return candidate.rect.top > viewport.height * 0.9 &&
                centreX > viewport.width * 0.35 && centreX < viewport.width * 0.65
        })

    return candidates.length === 1 ? candidates[0].text : ''
}
